import Container from "@bmi-digital/components/container";
import Section from "@bmi-digital/components/section";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { graphql } from "gatsby";
import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import BackToResults from "../../components/BackToResults";
import Breadcrumbs from "../../components/Breadcrumbs";
import ExploreBar, { ExploreBarData } from "../../components/ExploreBar";
import KeyAssetTypesDownloadSection from "../../components/KeyAssetTypesDownloadSection";
import Page from "../../components/Page";
import ProductOverview from "../../components/ProductOverview";
import RelatedProducts from "../../components/RelatedProducts";
import SampleOrderSection from "../../components/SampleOrderSection";
import ShareWidgetSection from "../../components/ShareWidgetSection";
import { useSiteContext } from "../../components/Site";
import { transformMediaSrc } from "../../utils/media";
import {
  getProductAttributes,
  transformImages
} from "../../utils/product-details-transforms";
import { PromoCard } from "../../components/Promo";
import ProductLeadBlock from "./components/product-lead-block";
import PdpCardsSection from "./components/pdp-cards";
import { ProductLeadBlockWrapper } from "./styles/productDetailsPage.styles";
import type { HomePageData, SiteData } from "../../components/Site";
import type { ShareWidgetSectionData } from "../../components/ShareWidgetSection";
import type { PageData } from "../../components/Page";
import type {
  ContactUsPageInfoCard,
  ProductListerPageInfoCard,
  SimplePageInfoCard
} from "../../components/PageInfo";
import type { SignupBlockData } from "../../components/SignupBlock";
import type { Product } from "../../types/pim";
import type { PayloadProductDetailsPage } from "../../types/payload";
import type { ResourcesData } from "../../components/Resources";
import type { MicroCopy as MicroCopyData } from "../../components/MicroCopy";
import type { CalculatorConfig } from "../../components/pitched-roof-calculator/types";
import type { HouseType } from "../../components/visualiser/Types";
import type { GeneralErrorData } from "../general-error";
import type { CountryNavigation, SampleOrder } from "../../components/Header";
import type { Visualiser } from "../../components/Visualiser";
import type { TitleWithContentData } from "../../components/TitleWithContent";
import type { RichTextData } from "../../components/RichText";

export type ProductDetailsPageData = Pick<
  PayloadProductDetailsPage,
  "pdpFixingToolTitle" | "pdpSpecificationTitle" | "pdpCardsTitle"
> & {
  pdpSignupBlock: readonly [SignupBlockData] | null;
  pdpShareWidget: readonly [ShareWidgetSectionData] | null;
  pdpSidebarItems: readonly TitleWithContentData[];
  pdpFixingToolDescription: RichTextData | null;
  pdpSpecificationDescription: RichTextData | null;
  pdpExploreBar: readonly [ExploreBarData] | never[];
  pdpCards:
    | readonly {
        value:
          | PromoCard
          | SimplePageInfoCard
          | ProductListerPageInfoCard
          | ContactUsPageInfoCard;
      }[]
    | null;
};

export type Props = {
  pageContext: {
    productCode: string;
    variantCodeToPathMap?: Record<string, string>;
  };
  data: {
    product: Product;
    payloadProductDetailsPage: ProductDetailsPageData;
    payloadResources: ResourcesData;
    payloadSite: SiteData;
    allPayloadMicroCopy: {
      nodes: MicroCopyData[];
    };
    payloadPitchedRoofCalculatorConfig: CalculatorConfig | null;
    allPayloadHouseModel: {
      nodes: HouseType[];
    };
    payloadHomePage: HomePageData;
    payloadGeneralError: GeneralErrorData;
    payloadCountryNavigation: CountryNavigation;
    payloadSampleOrder: SampleOrder | null;
    payloadVisualiser: Visualiser | null;
  };
};

const ProductDetailsPageContent = ({
  product,
  productCode,
  variantCodeToPathMap,
  sampleOrder,
  productDetailsPage,
  resources
}: {
  product: Product;
  productCode: Props["pageContext"]["productCode"];
  variantCodeToPathMap: Props["pageContext"]["variantCodeToPathMap"];
  sampleOrder: Props["data"]["payloadSampleOrder"];
  productDetailsPage: ProductDetailsPageData;
  resources: ResourcesData;
}) => {
  const siteContext = useSiteContext();
  const [queryParams, setQueryParams] = useState<string>("");
  const theme = useTheme();

  useEffect(() => {
    setQueryParams(window.location.search);
  }, []);

  const { getMicroCopy } = siteContext;

  const images = product.masterImage
    ? transformImages([product.masterImage, ...product.galleryImages])
    : transformImages([...product.galleryImages]);

  return (
    <>
      <Section
        backgroundColor="pearl"
        isSlim
        data-testid="breadcrumbs-section-top"
      >
        <BackToResults>
          <Breadcrumbs
            data={product.breadcrumbs}
            data-testid="product-details-page-breadcrumbs-top"
          />
        </BackToResults>
      </Section>
      <Container>
        <ProductOverview
          data={{
            name: product.name,
            brandCode: product.brand?.code,
            nobb: product.externalProductCode,
            images,
            videos: transformMediaSrc(product.videos, theme),
            goodBetterBest: product.goodBetterBest,
            attributes: getProductAttributes(
              product,
              siteContext.countryCode,
              {
                size: getMicroCopy(microCopy.PDP_OVERVIEW_SIZE),
                variantAttribute: getMicroCopy(
                  microCopy.PDP_OVERVIEW_VARIANT_ATTRIBUTE
                )
              },
              queryParams,
              variantCodeToPathMap
            ),
            variantCode: productCode,
            isRecaptchaShown: product.keyAssetDocuments
              ? Object.keys(product.keyAssetDocuments).length > 0
              : undefined,
            isNavigationToVisualiserAvailable: product.isVisualiserAvailable,
            modelDesignation: product.modelDesignation,
            empcoFootnotes: product.empcoFootnotes
          }}
        >
          <SampleOrderSection
            isSampleOrderAllowed={product.isSampleOrderAllowed}
            product={product}
            maximumSamples={sampleOrder?.maximumSamples}
            path={sampleOrder?.path}
          />
          {product.keyAssetDocuments &&
            Object.keys(product.keyAssetDocuments).length > 0 && (
              <KeyAssetTypesDownloadSection
                keyAssetDocuments={product.keyAssetDocuments}
              />
            )}
          {productDetailsPage.pdpShareWidget[0] && (
            <ShareWidgetSection
              data={{
                ...productDetailsPage.pdpShareWidget[0],
                isLeftAligned: true
              }}
              hasNoPadding={true}
            />
          )}
        </ProductOverview>
      </Container>
      <ProductLeadBlockWrapper
        backgroundColor="white"
        data-testid={`product-details-product-lead-block-section`}
      >
        <ProductLeadBlock
          product={product}
          sidebarItems={productDetailsPage.pdpSidebarItems}
          pdpFixingToolDescription={productDetailsPage.pdpFixingToolDescription}
          pdpFixingToolTitle={productDetailsPage.pdpFixingToolTitle}
          pdpSpecificationTitle={productDetailsPage.pdpSpecificationTitle}
          pdpSpecificationDescription={
            productDetailsPage.pdpSpecificationDescription
          }
          documentDisplayFormat={resources?.documentDisplayFormat}
        />
      </ProductLeadBlockWrapper>
      {product.relatedProducts && (
        <RelatedProducts
          countryCode={siteContext.countryCode}
          products={product.relatedProducts}
        />
      )}
      {productDetailsPage.pdpCardsTitle && productDetailsPage.pdpCards && (
        <PdpCardsSection
          resources={{
            pdpCards: productDetailsPage.pdpCards.map((card) => card.value),
            pdpCardsTitle: productDetailsPage.pdpCardsTitle
          }}
          countryCode={siteContext.countryCode}
        />
      )}
      {productDetailsPage.pdpExploreBar[0] && (
        <Section
          backgroundColor="alabaster"
          className="ExploreBar"
          data-testid={`product-details-product-explorer-bar-section`}
        >
          <ExploreBar data={productDetailsPage.pdpExploreBar[0]} />
        </Section>
      )}
      <Section
        backgroundColor="pearl"
        isSlim
        className="ProductBreadcrumbs"
        data-testid="breadcrumbs-section-bottom"
      >
        <Breadcrumbs
          data={product?.breadcrumbs}
          data-testid="product-details-page-breadcrumbs-bottom"
        />
      </Section>
    </>
  );
};

const ProductDetailsPage = ({ pageContext, data }: Props) => {
  const pageData: PageData = {
    breadcrumbs: data.product.breadcrumbs,
    signupBlock: data.payloadProductDetailsPage.pdpSignupBlock,
    meta: null,
    path: data.product.path
  };
  return (
    <Page
      brand={data.product.brand?.code}
      title={data.product.name}
      pageData={pageData}
      siteData={data.payloadSite}
      microCopy={data.allPayloadMicroCopy.nodes}
      pitchedRoofCalculatorConfig={data.payloadPitchedRoofCalculatorConfig}
      visualiserHouseTypes={data.allPayloadHouseModel.nodes}
      homePage={data.payloadHomePage}
      generalError={data.payloadGeneralError}
      countryNavigation={data.payloadCountryNavigation}
      sampleOrder={data.payloadSampleOrder}
      visualiser={data.payloadVisualiser}
      variantCodeToPathMap={pageContext?.variantCodeToPathMap}
      ogImageUrl={data.product.masterImage?.mainSource}
      variantProduct={data.product}
    >
      <ProductDetailsPageContent
        product={data.product}
        productCode={pageContext.productCode}
        productDetailsPage={data.payloadProductDetailsPage}
        sampleOrder={data.payloadSampleOrder}
        variantCodeToPathMap={pageContext.variantCodeToPathMap}
        resources={data.payloadResources}
      />
    </Page>
  );
};

export default ProductDetailsPage;

export const pageQuery = graphql`
  query ProductDetailsPage($productCode: String!) {
    product(code: { eq: $productCode }) {
      awardsAndCertificateDocuments {
        ...PIMAssetFragment
      }
      awardsAndCertificateImages {
        ...PIMAssetFragment
      }
      baseCode
      baseScoringWeight
      bimIframeUrl
      brand {
        ...PIMBrandFragment
      }
      breadcrumbs {
        id
        label
        slug
      }
      categories {
        ...CategoryFragment
      }
      classifications {
        ...ClassificationFragment
      }
      code
      colour
      colourMicrocopy
      path
      colourFamily
      description
      productDocuments {
        ...PIMDocumentCommonFragment
      }
      externalProductCode
      empcoFootnotes
      fixingToolIframeUrl
      galleryImages {
        ...PIMImageFragment
      }
      goodBetterBest
      guaranteesAndWarrantiesImages {
        ...PIMAssetFragment
      }
      guaranteesAndWarrantiesLinks {
        ...PIMAssetFragment
      }
      keyAssetDocuments {
        assetType
        documents {
          ...PIMDocumentFragment
        }
      }
      modelDesignation
      masterImage {
        ...PIMImageFragment
      }
      isSampleOrderAllowed
      isVisualiserAvailable
      materials
      measurements {
        ...MeasurementsFragment
      }
      name
      productBenefits
      relatedProducts {
        ...RelatedProductFragment
      }
      relatedVariants {
        code
        thumbnail
        colour
        colourFamily
        textureFamily
        materials
        measurements {
          ...MeasurementsFragment
        }
        variantAttribute
        path
      }
      seoTitle
      seoTags
      seoDescription
      specificationIframeUrl
      techDrawings {
        ...PIMImageFragment
      }
      textureFamily
      textureFamilyMicrocopy
      variantAttribute
      videos {
        ...PIMVideoFragment
      }
      weight {
        grossWeight {
          ...UnitValueFragment
        }
        netWeight {
          ...UnitValueFragment
        }
        weightPerPallet {
          ...UnitValueFragment
        }
        weightPerPiece {
          ...UnitValueFragment
        }
        weightPerSqm {
          ...UnitValueFragment
        }
      }
    }
    payloadProductDetailsPage {
      pdpSignupBlock {
        ...SignupBlockFragment
      }
      pdpShareWidget {
        ...ShareWidgetFragment
      }
      pdpSidebarItems {
        ...TitleWithContentFragment
      }
      pdpFixingToolDescription {
        ...RichTextFragment
      }
      pdpFixingToolTitle
      pdpSpecificationTitle
      pdpSpecificationDescription {
        ...RichTextFragment
      }
      pdpCardsTitle
      pdpCards {
        value {
          ... on PayloadPromo {
            ...PromoCardFragment
          }
          ... on PayloadPage {
            ...PageInfoCardFragment
          }
        }
      }
      pdpExploreBar {
        ...ExploreBarFragment
      }
    }
    payloadResources {
      ...ResourcesFragment
    }
    payloadSite {
      ...SiteFragment
    }
    allPayloadMicroCopy {
      nodes {
        ...MicroCopyFragment
      }
    }
    payloadPitchedRoofCalculatorConfig {
      ...PitchedRoofCalculatorFragment
    }
    allPayloadHouseModel {
      nodes {
        ...VisualiserHouseFragment
      }
    }
    payloadHomePage {
      ...HomePageFragment
    }
    payloadGeneralError {
      ...GeneralErrorFragment
    }
    payloadCountryNavigation {
      ...CountryNavigationFragment
    }
    payloadSampleOrder {
      ...SampleOrderFragment
    }
    payloadVisualiser {
      ...VisualiserFragment
    }
  }
`;
